import debounce from 'lodash.debounce'
import React, { useEffect, useMemo, useRef, useState } from 'react'
import { ChevronDownIcon } from 'src/editor/shared/icons/chevron-down-icon'
import { ChevronUpIcon } from 'src/editor/shared/icons/chevron-up-icon'
import { Input, InputProps } from '..'
import {
  clampValueToMinMaxRange,
  convertNumberValueToStringValue,
  formatStringValue,
  isEmptyOrMinus,
} from './utils'

const BUTTON_CLASSES =
  'flex h-2.5 w-5 shrink-0 cursor-pointer overflow-hidden outline-none'
const BUTTON_ICON_CLASSES = 'w-5 h-5 mt-[-5px] [&>path]:stroke-iconTertiary'

interface NumberInputProps
  extends Omit<InputProps, 'type' | 'value' | 'onChange'> {
  value: number
  min?: number
  max?: number
  hideControls?: boolean
  isDecimalAllowed?: boolean
  onChange: (value: number) => void
}

export const NumberInput = ({
  onChange,
  value,
  min = 0,
  max = 1000,
  endElement,
  hideControls = false,
  isDecimalAllowed = false,
  ...props
}: NumberInputProps) => {
  const holdIntervalRef = useRef<NodeJS.Timeout | null>(null)
  const holdTimeoutRef = useRef<NodeJS.Timeout | null>(null)
  const onChangeRef = useRef(onChange)

  const [valueState, setValueState] = useState<string>(
    convertNumberValueToStringValue(value),
  )

  useEffect(() => {
    onChangeRef.current = onChange
  }, [onChange])

  const debouncedOnChange = useMemo(
    () =>
      debounce((newValue: number) => {
        onChangeRef.current(newValue)
      }, 100),
    [],
  )

  const debouncedOnChangeSlow = useMemo(
    () =>
      debounce((newValue: number) => {
        onChangeRef.current(newValue)
      }, 1300),
    [],
  )

  const handleInputBlur = (valueString: string) =>
    handleInputChange(
      formatStringValue(valueString.replace(/\.+$/, ''), isDecimalAllowed),
    )

  const handleInputChange = (valueString: string) => {
    if (valueString.endsWith('.')) {
      debouncedOnChange.cancel()
      setValueState(valueString)
      return
    }
    if (isEmptyOrMinus(valueString)) {
      // Make long delay to allow user to input empty or minus-only values before inputting valid value
      debouncedOnChange.cancel()
      setValueState(valueString)
      debouncedOnChangeSlow(min < 0 ? 0 : min)
    } else if (0 < Number(valueString) && Number(valueString) < min) {
      /* Make long delay to allow user to input value with first digit less than min value
       * For example, user wants to to type 112, and min is 8. First digit is 1, which is less than 8.
       */
      debouncedOnChange.cancel()
      setValueState(valueString)
      debouncedOnChangeSlow(min)
    } else {
      const value = Number(valueString)
      const clampedValue = clampValueToMinMaxRange(value, min, max)
      setValueState(String(clampedValue))
      debouncedOnChangeSlow.cancel()
      debouncedOnChange(clampedValue)
    }
  }

  const changeValueByStep = (step: number) => {
    setValueState(prevValueString => {
      let value = Number(formatStringValue(prevValueString, isDecimalAllowed))
      if (isEmptyOrMinus(prevValueString)) {
        value = 0
      }
      const newValue = value + step
      const clampedValue = clampValueToMinMaxRange(newValue, min, max)
      debouncedOnChangeSlow.cancel()
      debouncedOnChange(clampedValue)
      return String(clampedValue)
    })
  }

  const startHoldIncrement = (action: () => void) => {
    // Executes the action immediately
    action()

    holdTimeoutRef.current = setTimeout(() => {
      // After 400ms delay, starts repeating every 100ms
      holdIntervalRef.current = setInterval(action, 100)
    }, 400)
  }

  const stopHoldIncrement = () => {
    if (holdTimeoutRef.current) {
      clearTimeout(holdTimeoutRef.current)
      holdTimeoutRef.current = null
    }
    if (holdIntervalRef.current) {
      clearInterval(holdIntervalRef.current)
      holdIntervalRef.current = null
    }
  }

  useEffect(() => {
    setValueState(convertNumberValueToStringValue(value))
  }, [value])

  useEffect(() => () => debouncedOnChange.cancel(), [debouncedOnChange])
  useEffect(() => () => debouncedOnChangeSlow.cancel(), [debouncedOnChangeSlow])

  return (
    <Input
      type="text" // We use type="text" because the "number" type works differently in different browsers
      min={min}
      max={max}
      value={valueState}
      onChange={(value: string) => {
        handleInputChange(formatStringValue(value, isDecimalAllowed))
      }}
      onBlur={handleInputBlur}
      endElement={
        <div className="ml-[-4px] flex items-center gap-0.5">
          {endElement}
          {!hideControls && (
            <div className="flex flex-col">
              {/* Increment button */}
              <button
                className={BUTTON_CLASSES}
                onMouseDown={() =>
                  startHoldIncrement(() => changeValueByStep(1))
                }
                onMouseUp={stopHoldIncrement}
                onMouseLeave={stopHoldIncrement}
                onTouchStart={() =>
                  startHoldIncrement(() => changeValueByStep(1))
                }
                onTouchEnd={stopHoldIncrement}
                type="button"
              >
                <ChevronUpIcon className={BUTTON_ICON_CLASSES} />
              </button>
              {/* Decrement button */}
              <button
                className={BUTTON_CLASSES}
                onMouseDown={() =>
                  startHoldIncrement(() => changeValueByStep(-1))
                }
                onMouseUp={stopHoldIncrement}
                onMouseLeave={stopHoldIncrement}
                onTouchStart={() =>
                  startHoldIncrement(() => changeValueByStep(-1))
                }
                onTouchEnd={stopHoldIncrement}
                type="button"
              >
                <ChevronDownIcon className={BUTTON_ICON_CLASSES} />
              </button>
            </div>
          )}
        </div>
      }
      {...props}
    />
  )
}
